import apiService, { ApiError } from './apiService';

/**
 * Asset Service
 * Handles all master asset-related API operations
 */
class AssetService {
  /**
   * Get list of assets with pagination, search, and filter
   * @param {Object} params - Filter and pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Items per page (default: 20)
   * @param {string} params.keyword - Search keyword (code, title, description)
   * @param {string} params.type - Asset type: 'T' (Text), 'F' (File)
   * @param {string} params.status - Asset status: 'A' (Active), 'N' (Inactive)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Asset list response
   */
  async getAssetList(params = {}, options = {}) {
    const { page = 1, size = 20, keyword, type, status } = params;

    try {
      const requestData = {
        page,
        size,
      };

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }
      if (type) {
        requestData.type = type;
      }
      if (status) {
        requestData.status = status;
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/admin/assets/list',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from asset list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch asset list:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to retrieve asset list',
        0,
        'ASSET_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get asset detail by ID
   * @param {string} id - Asset ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Asset detail response
   */
  async getAssetDetail(id, options = {}) {
    if (!id) {
      throw new ApiError('Asset ID is required', 400, 'VALIDATION_ERROR');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/assets/${id}`,
        {
          method: 'GET',
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from asset detail API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(`Failed to fetch asset detail for ID ${id}:`, error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to retrieve asset details',
        0,
        'ASSET_DETAIL_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Create a new asset
   * @param {Object} data - Asset data
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Created asset response
   */
  async createAsset(data = {}, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/admin/assets',
        {
          method: 'POST',
          data,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from asset create API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to create asset:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to create asset',
        0,
        'ASSET_CREATE_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Update an existing asset
   * @param {string} id - Asset ID
   * @param {Object} data - Asset updated data
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Updated asset response
   */
  async updateAsset(id, data = {}, options = {}) {
    if (!id) {
      throw new ApiError('Asset ID is required', 400, 'VALIDATION_ERROR');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/assets/${id}`,
        {
          method: 'PUT',
          data,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from asset update API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(`Failed to update asset ${id}:`, error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to update asset',
        0,
        'ASSET_UPDATE_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Delete an asset
   * @param {string} id - Asset ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Delete response
   */
  async deleteAsset(id, options = {}) {
    if (!id) {
      throw new ApiError('Asset ID is required', 400, 'VALIDATION_ERROR');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/assets/${id}`,
        {
          method: 'DELETE',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to delete asset ${id}:`, error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to delete asset',
        0,
        'ASSET_DELETE_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Upload asset file
   * @param {File} file - File object to upload
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Upload response containing path
   */
  async uploadFile(file, options = {}) {
    if (!file) {
      throw new ApiError('File is required', 400, 'VALIDATION_ERROR');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.skaiOneRequest(
        '/api/v1/admin/assets/upload',
        {
          method: 'POST',
          data: formData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from asset upload API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to upload asset file:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to upload file',
        0,
        'ASSET_UPLOAD_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get next auto-incremented version and sequence number for an asset code
   * @param {string} code - Asset code
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Next version response
   */
  async getNextVersion(code, options = {}) {
    if (!code) {
      throw new ApiError('Asset code is required', 400, 'VALIDATION_ERROR');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/assets/next-version/${encodeURIComponent(code)}`,
        {
          method: 'GET',
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from next version API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(`Failed to get next version for code ${code}:`, error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to get next asset version',
        0,
        'ASSET_NEXT_VERSION_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Toggle or update asset status (Active / Inactive)
   * @param {string} id - Asset ID
   * @param {string} status - New status ('A' or 'N')
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Updated asset response
   */
  async toggleStatus(id, status, options = {}) {
    if (!id) {
      throw new ApiError('Asset ID is required', 400, 'VALIDATION_ERROR');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/assets/${id}/status`,
        {
          method: 'PUT',
          data: { status },
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from toggle status API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(`Failed to toggle status for asset ${id}:`, error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        'Failed to toggle asset status',
        0,
        'ASSET_TOGGLE_STATUS_ERROR',
        { originalError: error.message }
      );
    }
  }
}

const assetService = new AssetService();
export default assetService;
export { AssetService };

